import { createAction, PayloadAction } from '@reduxjs/toolkit';
import {
  DashboardType,
  DashboardUiConfig,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { DashboardAppState } from './dashboards.app.reducer';
import { DashboardsReduxActions } from '../types/types';

export interface DashboardUi {
  showExportButtonSpinner: boolean;
  liveViewActive: boolean;
  fullScreenActive: boolean;
  settingsPanelActive: boolean;
  widgetDetailsPanelActive: boolean;
  widgetDetailsComponent: any;
  filtersPanelActive: boolean;
  hasFiltersToggle: boolean;
  hasWidgetsInErrorState: boolean;
}

export interface DashboardUiSliceState {
  ui: DashboardUi;
  configuration: DashboardUiConfig;
}

export const dashboardUiInitialSliceState: DashboardUiSliceState = {
  ui: {
    liveViewActive: false,
    fullScreenActive: false,
    settingsPanelActive: false,
    widgetDetailsPanelActive: false,
    widgetDetailsComponent: null,
    filtersPanelActive: true,
    hasFiltersToggle: true,
    showExportButtonSpinner: false,
    hasWidgetsInErrorState: false,
  },
  configuration: {
    dashboardType: DashboardType.HEALTH_DASHBOARD,
    smartViewsPanel: {
      title: 'Smart views',
      hasSearch: true,
      isOpen: true,
      visible: true,
    },
    settingsPanel: {
      visible: true,
      hasSearch: true,
    },
    liveView: {
      visible: true,
      usable: true,
    },
    canExport: {
      csv: true,
      pdf: true,
      report: true,
    },
    popUp: {
      shouldDisplayDiscard: true,
      shouldDisplayUnsavedChanges: true,
    },
  },
};
// SELECTORS
export const liveViewActiveStateSelector = (state: DashboardAppState): boolean => state.uiSlice.ui.liveViewActive;
export const fullScreenActiveStateSelector = (state: DashboardAppState): boolean => state.uiSlice.ui.fullScreenActive;
export const settingsPanelActiveStateSelector = (state: DashboardAppState): boolean =>
  state.uiSlice.ui.settingsPanelActive;
export const filtersPanelActiveStateSelector = (state: DashboardAppState): boolean =>
  state.uiSlice.ui.filtersPanelActive;

export const dashboardConfigurationSelector = (state: DashboardAppState): DashboardUiConfig =>
  state.uiSlice.configuration;
export const dashboardUiSelector = (state: DashboardAppState): DashboardUi => state.uiSlice.ui;

export const selectShouldDisplayDiscardPopUp = (state: DashboardAppState): boolean =>
  state.uiSlice.configuration.popUp.shouldDisplayDiscard;

export const selectShouldDisplayUnsavedChangesPopUp = (state: DashboardAppState): boolean =>
  state.uiSlice.configuration.popUp.shouldDisplayUnsavedChanges;

export const selectHasFiltersToggle = (state: DashboardAppState): boolean => state.uiSlice.ui.hasFiltersToggle;

export const selectShowExportButtonSpinner = (state: DashboardAppState): boolean =>
  state.uiSlice.ui.showExportButtonSpinner;

export const selectHasWidgetsInErrorState = (state: DashboardAppState): boolean =>
  state.uiSlice.ui.hasWidgetsInErrorState;

// ACTIONS
export const toggleLiveViewActiveState = createAction<boolean>(DashboardsReduxActions.TOGGLE_LIVE_VIEW);
export const toggleLiveViewActiveStateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.ui.liveViewActive = action.payload;
  state.uiSlice.ui.settingsPanelActive = false;
};

export const toggleFullscreenActiveState = createAction<boolean>(DashboardsReduxActions.TOGGLE_FULLSCREEN);
export const toggleFullscreenActiveStateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.ui.fullScreenActive = action.payload;
  state.uiSlice.ui.settingsPanelActive = false;
  state.uiSlice.ui.filtersPanelActive = !action.payload;
};

export const toggleExportButtonSpinner = createAction<boolean>(DashboardsReduxActions.TOGGLE_EXPORT_BUTTON_SPINNER);
export const toggleExportButtonSpinnerHandler = (state: DashboardAppState, action: PayloadAction<boolean>): void => {
  state.uiSlice.ui.showExportButtonSpinner = action.payload;
};

export const toggleHasWidgetsInErrorState = createAction<boolean>(
  DashboardsReduxActions.TOGGLE_HAS_WIDGETS_IN_ERROR_STATE
);
export const toggleHasWidgetsInErrorStateHandler = (state: DashboardAppState, action: PayloadAction<boolean>): void => {
  state.uiSlice.ui.hasWidgetsInErrorState = action.payload;
};

export const toggleSettingsPanelActiveState = createAction<boolean>(DashboardsReduxActions.TOGGLE_SETTINGS_PANEL);
export const toggleSettingsPanelActiveStateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.ui.settingsPanelActive = action.payload;
  state.uiSlice.ui.fullScreenActive = false;
  state.uiSlice.ui.liveViewActive = false;
};

export const toggleWidgetDetailsPanelActiveState = createAction<boolean>(DashboardsReduxActions.TOGGLE_WIDGET_DETAILS_PANEL);
export const toggleWidgetDetailsPanelActiveStateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.ui.widgetDetailsPanelActive = action.payload;
  state.uiSlice.ui.fullScreenActive = false;
  state.uiSlice.ui.liveViewActive = false;
};

export const toggleFiltersPanelActiveState = createAction<boolean>(DashboardsReduxActions.TOGGLE_FILTERS_PANEL);
export const toggleFiltersPanelActiveStateActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.ui.filtersPanelActive = action.payload;
};

export const setDashboardConfiguration = createAction<DashboardUiConfig>(
  DashboardsReduxActions.SET_DASHBOARD_CONFIGURATION
);
export const setDashboardConfigurationActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<DashboardUiConfig>
): void => {
  state.uiSlice.configuration = { ...action.payload };
};

export const disableLiveView = createAction(DashboardsReduxActions.DISABLE_LIVE_VIEW_FUNCTIONALITY);
export const disableLiveViewActionHandler = (state: DashboardAppState): void => {
  state.uiSlice.configuration.liveView = {
    visible: false,
    usable: false,
  };
};
